import { makeObservable, observable, action, computed, reaction, runInAction } from '../../common/stores/mobx-config.js';
// import FamilyIndices from './indices/familyIndices'; // ✅ SUPPRIMÉ - Relations directes utilisées
import { TAGS } from './gedcomConstantsStore.js';
import { storeEvents, EVENTS } from '../../common/stores/storeEvents.js';
import { wrapIndividualWithFormatters } from '../formatters/individualFormatters.js';
import { logger } from '../../utils/debug/logger.js';
import processedCacheService from '../../utils/processedCacheService.js';

class GedcomDataStore {
    sourceData = [];
    individualsCache = new Map();
    familiesCache = new Map(); // 🆕 Cache des familles compressé avec événements
    notesCache = new Map(); // 🆕 Cache des notes centralisé
    sourcesCache = new Map(); // 🆕 Cache des sources centralisé
    mediasCache = new Map(); // 🆕 Cache des médias centralisé
    repositoriesCache = new Map(); // 🆕 Cache des dépôts centralisé
    _hierarchy = null;
    _descendantsTree = null;
    _descendantsCacheLazy = new Map(); // 🆕 Cache lazy des arbres de descendants par personne
    familyEvents = [];
    isFileUploaded = false;
    reactionDisposers = new Map();
    isProcessing = false;
    isRestoringFromCache = false; // Flag pour tracker la restauration cache
    isDirectImport = false; // Flag pour tracker les imports directs (sans upload R2)
    // indices = new FamilyIndices(); // ✅ SUPPRIMÉ - Relations directes dans individualsCache

    constructor() {
        logger.gedcom.debug('GedcomDataStore constructor called');
        // logger.gedcom.debug('GedcomDataStore initializing with familyIndices:', FamilyIndices); // ✅ SUPPRIMÉ
        
        // this.familyIndices = FamilyIndices; // ✅ SUPPRIMÉ - Relations directes utilisées

        makeObservable(this, {
            sourceData: observable,
            individualsCache: observable,
            familiesCache: observable, // 🆕 Cache des familles
            notesCache: observable, // 🆕 Cache des notes
            sourcesCache: observable, // 🆕 Cache des sources
            mediasCache: observable, // 🆕 Cache des médias
            repositoriesCache: observable, // 🆕 Cache des dépôts
            _hierarchy: observable.ref,
            _descendantsTree: observable.ref,
            _descendantsCacheLazy: observable, // 🆕 Cache lazy des arbres de descendants
            // familyIndices: observable.ref, // ✅ SUPPRIMÉ - Relations directes utilisées
            familyEvents: observable,
            isFileUploaded: observable,
            isRestoringFromCache: observable,
            isDirectImport: observable,
            reactionDisposers: observable, // Nouveau
            
            // Actions
            setSourceData: action,
            clearSourceData: action,
            setIndividualsCache: action,
            setFamiliesCache: action, // 🆕 Nouvelle action
            setNotesCache: action, // 🆕 Nouvelle action
            setSourcesCache: action, // 🆕 Nouvelle action
            setMediasCache: action, // 🆕 Nouvelle action
            setRepositoriesCache: action, // 🆕 Nouvelle action
            setHierarchy: action,
            setDescendantsTree: action,
            clearDescendantsCache: action, // 🆕 Vider le cache lazy des descendants
            addFamilyEvent: action,
            setFamilyEvents: action,
            setFileUploaded: action,
            setRestoringFromCache: action,
            setDirectImport: action,
            clearAllState: action,
            addReactionDisposer: action, // Nouveau
            removeReactionDisposer: action, // Nouveau
            clearReactions: action, // Nouveau
            
            // Computed
            totalIndividuals: computed,
            totalFamilies: computed, // 🆕 Nombre de familles
            totalNotes: computed, // 🆕 Nombre de notes
            hasData: computed,
            familyTreeData: computed
        });

        reaction(
            () => this.sourceData,
            (json) => {
                if (json && json.length > 0) {
                    logger.gedcom.group('📥 Source JSON avant traitement');
                    logger.gedcom.debug('Nombre d\'entrées:', json.length);
                    // Déconstruire le proxy du JSON source
                    // logger.gedcom.debug('Contenu:', JSON.parse(JSON.stringify(json)));
                    logger.gedcom.groupEnd();
        
                    // ❌ OBSOLÈTE: buildIndividualsCache n'est plus utilisé avec read-gedcom-geneafan
                    logger.gedcom.warn('⚠️ sourceJson setter est obsolète - utiliser setIndividualsCache() directement');
                    this.clearSourceData();
        
                    // Log détaillé du cache
                    logger.gedcom.group('🗂️ Cache des individus après construction');
                    logger.gedcom.debug('Nombre total d\'individus:', this.individualsCache.size);
        
                    const enableConsoleLogging = false; // Vous pouvez définir cette variable selon vos besoins
        
                    this.individualsCache.forEach((individual, id) => {
                        // Déconstruire le proxy de chaque individu
                        const cleanIndividual = JSON.parse(JSON.stringify(individual));
        
                        if (enableConsoleLogging) {
                            logger.gedcom.group(`👤 ${cleanIndividual.name || ''} ${cleanIndividual.surname || ''} (${id})`);
        
                            if (cleanIndividual.individualEvents?.length > 0) {
                                logger.gedcom.group('📅 Événements:');
                                cleanIndividual.individualEvents.forEach(event => {
                                    logger.gedcom.debug({
                                        type: event.type,
                                        date: event.date,
                                        town: event.town,
                                        formatted: event.formatted || ''
                                    });
                                });
                                logger.gedcom.groupEnd();
                            } else {
                                logger.gedcom.debug('Aucun événement');
                            }
        
                            logger.gedcom.groupEnd();
                        }
                    });
                    logger.gedcom.groupEnd();
                }
            },
            {
                name: 'sourceDataToCache',
                fireImmediately: false
            }
        );

        // Écouter les événements d'import pour définir le type d'import
        storeEvents.subscribe(EVENTS.IMPORT.STARTED, ({ type, reason }) => {
            logger.gedcom.debug(`🎯 Import démarré: type=${type}, reason=${reason}`);
            
            // Marquer comme import direct si nécessaire
            const isDirectImport = type === 'direct' || type === 'cache_restore';
            this.setDirectImport(isDirectImport);
            
            if (type === 'cache_restore') {
                this.setRestoringFromCache(true);
            }
        });
    }

    addIndividual = (id, data) => {
        runInAction(() => {
            this.individualsCache.set(id, data);
        });
    };


    // Source Data Methods
    setSourceData = (newSourceData) => {
        runInAction(() => {
            this.sourceData = newSourceData;
        });
    };

    clearSourceData = () => {
        runInAction(() => {
            this.sourceData = [];
        });
    };

    // Individuals Cache Methods
    setIndividualsCache = (newCache) => {
        runInAction(() => {
            this.individualsCache = new Map(newCache);
        });
    };

    getIndividualsCache = () => {
        return this.individualsCache;
    };

    // 🆕 Families Cache Methods
    setFamiliesCache = (newCache) => {
        runInAction(() => {
            this.familiesCache = new Map(newCache);
        });
    };

    getFamiliesCache = () => {
        return this.familiesCache;
    };

    // 🆕 Notes Cache Methods - Centralisé pour éviter les variables globales
    setNotesCache = (newCache) => {
        runInAction(() => {
            this.notesCache = new Map(newCache);
            logger.gedcom.debug('📝 Notes cache mis à jour:', this.notesCache.size, 'notes');
        });
    };

    getNotesCache = () => {
        return this.notesCache;
    };

    // 🆕 Sources Cache Methods
    setSourcesCache = (newCache) => {
        runInAction(() => {
            this.sourcesCache = new Map(newCache);
            logger.gedcom.debug('📚 Sources cache mis à jour:', this.sourcesCache.size, 'sources');
        });
    };

    getSourcesCache = () => {
        return this.sourcesCache;
    };

    // 🆕 Medias Cache Methods
    setMediasCache = (newCache) => {
        runInAction(() => {
            this.mediasCache = new Map(newCache);
            logger.gedcom.debug('🖼️ Medias cache mis à jour:', this.mediasCache.size, 'médias');
        });
    };

    getMediasCache = () => {
        return this.mediasCache;
    };

    // 🆕 Repositories Cache Methods
    setRepositoriesCache = (newCache) => {
        runInAction(() => {
            this.repositoriesCache = new Map(newCache);
            logger.gedcom.debug('🏛️ Repositories cache mis à jour:', this.repositoriesCache.size, 'dépôts');
        });
    };

    getRepositoriesCache = () => {
        return this.repositoriesCache;
    };

    getIndividualsList = () => {
        return Array.from(this.individualsCache.values());
    };

    getIndividual = (id) => {
        const individual = this.individualsCache.get(id);
        if (!individual) return null;
        // Wrapper qui ajoute les propriétés formatées à la demande
        // TEMPORAIRE: Stats désactivées jusqu'à correction complète
        return wrapIndividualWithFormatters(individual, this.individualsCache);
    };

    // Hierarchy Methods
    setHierarchy = (newHierarchy) => {
        logger.gedcom.debug('📊 setHierarchy appelé avec:', newHierarchy);
        runInAction(() => {
            const oldHierarchy = this._hierarchy;
            this._hierarchy = newHierarchy;
            logger.gedcom.debug('✨ Hiérarchie mise à jour:', {
                old: oldHierarchy ? 'présent' : 'null',
                new: newHierarchy ? 'présent' : 'null',
                changed: oldHierarchy !== newHierarchy
            });
        });
    };

    getHierarchy = () => {
        const hierarchy = this._hierarchy;
        logger.gedcom.debug('🔍 getHierarchy appelé, retourne:', hierarchy ? 'présent' : 'null');
        return hierarchy;
    };

    setDescendantsTree = (newDescendantsTree) => {
        logger.gedcom.debug('🌳 setDescendantsTree appelé avec:', newDescendantsTree);
        runInAction(() => {
            const oldTree = this._descendantsTree;
            this._descendantsTree = newDescendantsTree;
            logger.gedcom.debug('✨ Arbre des descendants mis à jour:', {
                old: oldTree ? 'présent' : 'null',
                new: newDescendantsTree ? 'présent' : 'null',
                changed: oldTree !== newDescendantsTree
            });
        });
    };

    getDescendantsTree = () => {
        const tree = this._descendantsTree;
        logger.gedcom.debug('🔍 getDescendantsTree appelé, retourne:', tree ? 'présent' : 'null');
        return tree;
    };

    /**
     * Obtient l'arbre des descendants pour une personne spécifique avec mémoïsation
     * @param {string} personId - ID de la personne
     * @returns {Object|null} Arbre des descendants avec numérotation d'Aboville
     */
    getDescendantsTreeForPerson = (personId) => {
        if (!personId) return null;

        // Vérifier le cache lazy
        if (this._descendantsCacheLazy.has(personId)) {
            logger.ui.debug(`✅ Descendants cache HIT for ${personId}`);
            return this._descendantsCacheLazy.get(personId);
        }

        // Cache MISS - calculer l'arbre
        logger.ui.debug(`❌ Descendants cache MISS for ${personId}, calculating...`);
        const startTime = performance.now();

        // Import dynamique pour éviter la dépendance circulaire
        const rootPersonStore = require('../../common/stores/rootPersonStore.js').default;
        const tree = rootPersonStore.buildDescendantsTree(personId);

        const calcTime = performance.now() - startTime;
        logger.ui.perf(`Descendants tree calculated in ${calcTime.toFixed(2)}ms for ${personId}`);

        // Stocker dans le cache lazy
        runInAction(() => {
            this._descendantsCacheLazy.set(personId, tree);
        });

        // Log des stats du cache
        logger.ui.debug(`📊 Descendants cache size: ${this._descendantsCacheLazy.size} persons`);

        return tree;
    };

    /**
     * Vide le cache lazy des descendants (appelé lors du changement de GEDCOM)
     */
    clearDescendantsCache = () => {
        runInAction(() => {
            this._descendantsCacheLazy.clear();
            logger.ui.debug('🧹 Descendants lazy cache cleared');
        });
    };

    // Family Events Methods
    addFamilyEvent = (event) => {
        runInAction(() => {
            this.familyEvents.push(event);
        });
    };

    setFamilyEvents = (events) => {
        runInAction(() => {
            this.familyEvents = [...events];
        });
    };

    getFamilyEvents = () => {
        return this.familyEvents;
    };

    clearFamilyEvents = () => {
        runInAction(() => {
            this.familyEvents = [];
        });
    };


    // File Upload Status Methods
    setFileUploaded = (status) => {
        runInAction(() => {
            this.isFileUploaded = status;
        });
    };

    setRestoringFromCache = (status) => {
        runInAction(() => {
            this.isRestoringFromCache = status;
            logger.gedcom.debug(`🔄 GedcomDataStore: isRestoringFromCache = ${status}`);
        });
    };

    setDirectImport = (status) => {
        runInAction(() => {
            this.isDirectImport = status;
            logger.gedcom.debug(`📥 GedcomDataStore: isDirectImport = ${status}`);
        });
    };

    getFileUploaded = () => {
        return this.isFileUploaded;
    };

    // Nouvelles méthodes pour la gestion des réactions
    addReactionDisposer = (id, trackedFn, effectFn, options = {}) => {
        logger.gedcom.debug('📝 Ajout d\'une réaction:', id);
        runInAction(() => {
            // Nettoyer une réaction existante
            if (this.reactionDisposers.has(id)) {
                logger.gedcom.debug('🧹 Nettoyage de l\'ancienne réaction:', id);
                this.reactionDisposers.get(id)();
                this.reactionDisposers.delete(id);
            }
    
            // Vérifications
            if (typeof trackedFn !== 'function' || typeof effectFn !== 'function') {
                logger.gedcom.error('❌ trackedFn et effectFn doivent être des fonctions');
                return;
            }
    
            try {
                // Wrapper le trackedFn pour le debugging
                const wrappedTrackedFn = () => {
                    const result = trackedFn();
                    logger.gedcom.debug(`🔍 trackedFn ${id} retourne:`, result);
                    return result;
                };
    
                // Wrapper le effectFn pour le debugging
                const wrappedEffectFn = (value) => {
                    logger.gedcom.debug(`🎯 effectFn ${id} appelé avec:`, value);
                    return effectFn(value);
                };
    
                // Créer la réaction avec les wrappers
                const disposer = reaction(
                    wrappedTrackedFn,
                    wrappedEffectFn,
                    {
                        ...options,
                        onError: (error) => {
                            logger.gedcom.error(`🚨 Erreur dans la réaction ${id}:`, error);
                            if (options.onError) options.onError(error);
                        }
                    }
                );
    
                this.reactionDisposers.set(id, disposer);
                logger.gedcom.debug('✅ Réaction ajoutée avec succès:', id);
            } catch (error) {
                logger.gedcom.error('❌ Erreur lors de la création de la réaction:', error);
            }
        });
    };

    removeReactionDisposer = (id) => {
        runInAction(() => {
            if (this.reactionDisposers.has(id)) {
                this.reactionDisposers.get(id)();
                this.reactionDisposers.delete(id);
            }
        });
    };

    clearReactions = () => {
        runInAction(() => {
            this.reactionDisposers.forEach(disposer => disposer());
            this.reactionDisposers.clear();
        });
    };

    // Reset State
    clearAllState() {
        logger.gedcom.debug('Clearing all state in GedcomDataStore');
        runInAction(() => {
            this.sourceData = [];
            this.individualsCache = new Map();
            this.familiesCache = new Map(); // 🆕 Reset families cache
            this.notesCache = new Map(); // 🆕 Reset notes cache
            this.sourcesCache = new Map(); // 🆕 Reset sources cache
            this.mediasCache = new Map(); // 🆕 Reset medias cache
            this.repositoriesCache = new Map(); // 🆕 Reset repositories cache
            this._hierarchy = null;
            this._descendantsTree = null;
            this._descendantsCacheLazy.clear(); // 🆕 Vider le cache lazy des descendants
            this.familyEvents = [];
            this.isFileUploaded = false;
            this.isDirectImport = false;
            this.isRestoringFromCache = false;
            
            // ✅ SUPPRIMÉ - FamilyIndices obsolètes
            // Relations directes (f, m, s) stockées dans individualsCache
            logger.gedcom.debug('✅ Clearing completed - Using direct relations instead of indices');
            
            this.clearReactions();
        });
    }

    // Computed Properties
    get totalIndividuals() {
        return this.individualsCache.size;
    }

    // 🆕 Computed Properties pour familiesCache
    get totalFamilies() {
        return this.familiesCache.size;
    }

    // 🆕 Computed: nombre total de notes
    get totalNotes() {
        return this.notesCache.size;
    }

    get hasData() {
        return this.sourceData.length > 0;
    }

    get familyTreeData() {
        return this.getFamilyTreeData();
    }

    // Family Tree Data
    getFamilyTreeData = () => {
        // Phase 11: Utiliser entries() pour récupérer [id, data] directement
        return Array.from(this.individualsCache.entries()).map(([id, data]) => ({
            id: id, // Phase 11: utiliser la clé du Map directement 
            fid: data.f,
            mid: data.m,
            pids: (data.e?.filter(e => e.type === 'marriage')?.map(e => e.spouseId) || []).filter(Boolean),
            name: `${data.name || ''} ${data.surname || ''}`,
            birthDate: data.birthDate,
            deathDate: data.deathYear,
            gender: data.gender,
            display: true
        }));
    };


    /**
     * Programme la génération asynchrone du cache processed
     * Appelé après construction complète du cache individus
     * 🚀 V1.1: Ne génère PAS le cache compressé pour les imports directs (économise CPU)
     * La compression n'est faite que si un écouteur PROCESSED_READY attend (upload R2)
     */
    scheduleProcessedCacheGeneration() {
        // 🚀 OPTIMISATION: Skip la compression pour les imports directs (pas d'upload R2)
        if (this.isDirectImport) {
            logger.gedcom.info('📥 Import direct détecté - compression cache processed ignorée (pas d\'upload R2)');
            // L'intégration AI n'est pas proposée ici : fichier non sauvegardé
            // (voir aiNotesIntegration.setupExtractionConsent())
            return;
        }

        // Génération différée pour ne pas bloquer l'UI (uniquement si upload R2 demandé)
        setTimeout(async () => {
            try {
                logger.gedcom.debug('🚀 Début génération cache processed pour upload R2...');

                // Debug: vérifier l'état avant génération
                logger.gedcom.debug('📊 État avant génération cache:', {
                    individualsCount: this.individualsCache?.size || 0,
                    hasIndividualsCache: !!this.individualsCache,
                    canGenerate: processedCacheService.canGenerateCache()
                });

                // Vérification que le cache peut être généré
                if (!processedCacheService.canGenerateCache()) {
                    logger.gedcom.warn('⚠️ Cache processed: Données insuffisantes pour génération');
                    return;
                }

                // Génération du cache processed (compression dans Web Worker)
                const processedCache = await processedCacheService.generateProcessedCache();

                // Émettre un événement pour notifier que le cache processed est prêt
                storeEvents.emit(EVENTS.CACHE.PROCESSED_READY, processedCache);

                logger.gedcom.debug('✅ Cache processed généré et prêt pour upload');

                // L'intégration AI est déclenchée par consentement utilisateur
                // via aiNotesIntegration.setupExtractionConsent() (écoute CACHE.BUILT
                // et FILE.CURRENT_CHANGED, mémorise le choix par fileId)

            } catch (error) {
                logger.gedcom.warn('⚠️ Échec génération/upload cache processed:', error);
                // Non-bloquant: l'utilisateur peut continuer normalement
            }
        }, 2000); // 2 secondes après construction du cache
    }
}

const gedcomDataStore = new GedcomDataStore();

// 🖥️ Exposer le store globalement pour accès console
if (typeof window !== 'undefined') {
    window.gedcomDataStore = gedcomDataStore;
    
    // 🚀 Méthodes simples et directes pour export rapide
    window.getCacheJSON = (pretty = true) => {
        if (!gedcomDataStore.individualsCache || gedcomDataStore.individualsCache.size === 0) {
            logger.gedcom.error('❌ Aucun cache trouvé. Chargez d\'abord un fichier GEDCOM.');
            return null;
        }
        const cacheObj = Object.fromEntries(gedcomDataStore.individualsCache);
        const json = pretty ? JSON.stringify(cacheObj, null, 2) : JSON.stringify(cacheObj);
        
        // Afficher proprement dans la console sans les \n
        if (pretty) {
            console.log(json);
        }
        
        return json;
    };
    
    window.copyCacheSimple = () => {
        if (!gedcomDataStore.individualsCache || gedcomDataStore.individualsCache.size === 0) {
            logger.gedcom.error('❌ Aucun cache trouvé.');
            return false;
        }
        const cacheObj = Object.fromEntries(gedcomDataStore.individualsCache);
        const json = JSON.stringify(cacheObj, null, 2);
        copy(json);
        logger.gedcom.info('✅ Cache copié! ' + gedcomDataStore.individualsCache.size + ' individus');
        return true;
    };
    
    window.showIndividual = (pointer) => {
        const individual = gedcomDataStore.individualsCache.get(pointer);
        if (individual) {
            // Utiliser console.log avec l'objet directement pour un affichage propre
            console.log(pointer + ':', individual);
            return individual;
        } else {
            logger.gedcom.error(`❌ Individu ${pointer} non trouvé`);
            return null;
        }
    };
    
    window.showFirst = (count = 10) => {
        const entries = Array.from(gedcomDataStore.individualsCache.entries()).slice(0, count);
        const obj = Object.fromEntries(entries);
        // Afficher l'objet directement pour éviter les \n
        console.log('Premiers ' + count + ' individus:', obj);
        return obj;
    };
    
    // Méthode pour afficher proprement le cache sans les \n
    window.printCache = () => {
        if (!gedcomDataStore.individualsCache || gedcomDataStore.individualsCache.size === 0) {
            logger.gedcom.error('❌ Aucun cache trouvé.');
            return;
        }
        const cacheObj = Object.fromEntries(gedcomDataStore.individualsCache);
        logger.gedcom.info('📦 Cache complet (' + gedcomDataStore.individualsCache.size + ' individus):');
        console.log(cacheObj);  // Keep console.log for object display
        return cacheObj;
    };
    
    // 💾 Sauvegarder le cache dans un fichier JSON propre
    window.saveCache = (filename = 'individualsCache.json') => {
        if (!gedcomDataStore.individualsCache || gedcomDataStore.individualsCache.size === 0) {
            logger.gedcom.error('❌ Aucun cache trouvé.');
            return;
        }
        
        const cacheObj = Object.fromEntries(gedcomDataStore.individualsCache);
        const jsonString = JSON.stringify(cacheObj, null, 2);
        
        // Créer un blob et télécharger
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        logger.gedcom.info('✅ Cache sauvegardé dans ' + filename);
        logger.gedcom.info('📊 ' + gedcomDataStore.individualsCache.size + ' individus exportés');
        return true;
    };

    // 🔍 Bus d'événements exposé pour le debug/diagnostic (ex. compter les
    // abonnements par événement afin de détecter une fuite de disposers :
    // [...storeEvents.listeners].map(([ev, s]) => [ev, s.size])).
    window.storeEvents = storeEvents;

    // 🏘️ Fonctions d'export pour familyTownsStore
    window.getFamilyTownsJSON = async (pretty = true) => {
        try {
            // Import dynamique pour éviter les dépendances circulaires
            const { default: familyTownsStore } = await import('../../tabs/familyMap/stores/familyTownsStore.js');
            
            if (!familyTownsStore.townsData || familyTownsStore.townsData.size === 0) {
                logger.gedcom.error('❌ Aucune donnée familyTownsStore trouvée. Chargez d\'abord un fichier GEDCOM.');
                return null;
            }
            
            const townsObj = Object.fromEntries(familyTownsStore.townsData);
            const json = pretty ? JSON.stringify(townsObj, null, 2) : JSON.stringify(townsObj);
            
            // Afficher proprement dans la console sans les \n
            if (pretty) {
                console.log(json);
            }
            
            return json;
        } catch (error) {
            console.error('❌ Erreur lors de l\'export familyTownsStore:', error);
            return null;
        }
    };
    
    window.copyFamilyTownsSimple = async () => {
        try {
            const { default: familyTownsStore } = await import('../../tabs/familyMap/stores/familyTownsStore.js');
            
            if (!familyTownsStore.townsData || familyTownsStore.townsData.size === 0) {
                console.error('❌ Aucune donnée familyTownsStore trouvée.');
                return false;
            }
            
            const townsObj = Object.fromEntries(familyTownsStore.townsData);
            const json = JSON.stringify(townsObj, null, 2);
            copy(json);
            console.log('✅ FamilyTowns copié! ' + familyTownsStore.townsData.size + ' villes');
            return true;
        } catch (error) {
            console.error('❌ Erreur lors de la copie familyTownsStore:', error);
            return false;
        }
    };
    
    window.printFamilyTowns = async () => {
        try {
            const { default: familyTownsStore } = await import('../../tabs/familyMap/stores/familyTownsStore.js');
            
            if (!familyTownsStore.townsData || familyTownsStore.townsData.size === 0) {
                console.error('❌ Aucune donnée familyTownsStore trouvée.');
                return;
            }
            
            const townsObj = Object.fromEntries(familyTownsStore.townsData);
            console.log('🏘️ FamilyTowns complet (' + familyTownsStore.townsData.size + ' villes):');
            console.log(townsObj);
            return townsObj;
        } catch (error) {
            console.error('❌ Erreur lors de l\'affichage familyTownsStore:', error);
            return null;
        }
    };
    
    // 💾 Sauvegarder familyTownsStore dans un fichier JSON propre
    window.saveFamilyTowns = async (filename = 'familyTownsStore.json') => {
        try {
            const { default: familyTownsStore } = await import('../../tabs/familyMap/stores/familyTownsStore.js');
            
            if (!familyTownsStore.townsData || familyTownsStore.townsData.size === 0) {
                console.error('❌ Aucune donnée familyTownsStore trouvée.');
                return;
            }
            
            const townsObj = Object.fromEntries(familyTownsStore.townsData);
            const jsonString = JSON.stringify(townsObj, null, 2);
            
            // Créer un blob et télécharger
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            console.log('✅ FamilyTowns sauvegardé dans ' + filename);
            console.log('🏘️ ' + familyTownsStore.townsData.size + ' villes exportées');
            return true;
        } catch (error) {
            console.error('❌ Erreur lors de la sauvegarde familyTownsStore:', error);
            return false;
        }
    };
    
    // 📝 Fonctions d'export pour notesCache
    window.getNotesJSON = (pretty = true) => {
        const notesCache = gedcomDataStore.notesCache; // Utilise le cache centralisé
        if (!notesCache || notesCache.size === 0) {
            console.error('❌ Aucune donnée notesCache trouvée. Chargez d\'abord un fichier GEDCOM.');
            return null;
        }
        
        const notesObj = Object.fromEntries(notesCache);
        const json = pretty ? JSON.stringify(notesObj, null, 2) : JSON.stringify(notesObj);
        
        // Afficher proprement dans la console sans les \n
        if (pretty) {
            console.log(json);
        }
        
        return json;
    };
    
    window.copyNotesSimple = () => {
        const notesCache = gedcomDataStore.notesCache;
        if (!notesCache || notesCache.size === 0) {
            console.error('❌ Aucune donnée notesCache trouvée.');
            return false;
        }
        
        const notesObj = Object.fromEntries(notesCache);
        const json = JSON.stringify(notesObj, null, 2);
        copy(json);
        logger.gedcom.info('✅ Notes cache copié! ' + notesCache.size + ' notes');
        return true;
    };
    
    window.printNotesCache = () => {
        const notesCache = gedcomDataStore.notesCache;
        if (!notesCache || notesCache.size === 0) {
            console.error('❌ Aucune donnée notesCache trouvée.');
            return;
        }
        
        const notesObj = Object.fromEntries(notesCache);
        console.log('📝 Notes cache complet (' + notesCache.size + ' notes):');
        console.log(notesObj);
        return notesObj;
    };
    
    // 💾 Sauvegarder notesCache dans un fichier JSON propre
    window.saveNotesCache = (filename = 'notesCache.json') => {
        const notesCache = gedcomDataStore.notesCache;
        if (!notesCache || notesCache.size === 0) {
            console.error('❌ Aucune donnée notesCache trouvée.');
            return;
        }
        
        const notesObj = Object.fromEntries(notesCache);
        const jsonString = JSON.stringify(notesObj, null, 2);
        
        // Créer un blob et télécharger
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ Notes cache sauvegardé dans ' + filename);
        console.log('📝 ' + notesCache.size + ' notes exportées');
        return true;
    };
    
    // 🏠 Fonctions d'export pour familiesCache
    window.getFamiliesCacheJSON = (pretty = true) => {
        if (!gedcomDataStore.familiesCache || gedcomDataStore.familiesCache.size === 0) {
            console.error('❌ Aucune familiesCache trouvée. Chargez d\'abord un fichier GEDCOM.');
            return null;
        }
        const familiesObj = Object.fromEntries(gedcomDataStore.familiesCache);
        const json = pretty ? JSON.stringify(familiesObj, null, 2) : JSON.stringify(familiesObj);
        
        // Afficher proprement dans la console sans les \n
        if (pretty) {
            console.log(json);
        }
        
        return json;
    };
    
    window.copyFamiliesCacheSimple = () => {
        if (!gedcomDataStore.familiesCache || gedcomDataStore.familiesCache.size === 0) {
            console.error('❌ Aucune familiesCache trouvée.');
            return false;
        }
        const familiesObj = Object.fromEntries(gedcomDataStore.familiesCache);
        const json = JSON.stringify(familiesObj, null, 2);
        copy(json);
        logger.gedcom.info('✅ FamiliesCache copié! ' + gedcomDataStore.familiesCache.size + ' familles');
        return true;
    };
    
    window.printFamiliesCache = () => {
        if (!gedcomDataStore.familiesCache || gedcomDataStore.familiesCache.size === 0) {
            console.error('❌ Aucune familiesCache trouvée.');
            return;
        }
        const familiesObj = Object.fromEntries(gedcomDataStore.familiesCache);
        console.log('🏠 FamiliesCache complet (' + gedcomDataStore.familiesCache.size + ' familles):');
        console.log(familiesObj);
        return familiesObj;
    };
    
    window.showFamily = (pointer) => {
        const family = gedcomDataStore.familiesCache.get(pointer);
        if (family) {
            console.log(pointer + ':', family);
            return family;
        } else {
            logger.gedcom.error(`❌ Famille ${pointer} non trouvée`);
            return null;
        }
    };
    
    // 🚀 PERFORMANCE O(1): Trouver les familles d'une personne
    window.getFamiliesForPerson = (personId) => {
        const individual = gedcomDataStore.individualsCache.get(personId);
        if (!individual) {
            logger.gedcom.error(`❌ Individu ${personId} non trouvé`);
            return [];
        }
        
        const familyIds = individual.families || [];
        console.log(`👥 ${personId} appartient à ${familyIds.length} famille(s):`);
        
        const families = [];
        familyIds.forEach(familyId => {
            const family = gedcomDataStore.familiesCache.get(familyId);
            if (family) {
                console.log(`  ${familyId}:`, family);
                families.push({ id: familyId, data: family });
            }
        });
        
        return families;
    };
    
    // 🚀 PERFORMANCE O(1): Analyser les relations familiales avec mariages multiples
    window.analyzeFamilyRelations = (personId) => {
        const individual = gedcomDataStore.individualsCache.get(personId);
        if (!individual) {
            logger.gedcom.error(`❌ Individu ${personId} non trouvé`);
            return null;
        }
        
        console.log(`🔍 Relations familiales de ${personId} (${individual.fn}):`);
        console.log(`  Père: ${individual.f || 'Inconnu'}`);
        console.log(`  Mère: ${individual.m || 'Inconnue'}`);
        console.log(`  Conjoint(s): ${Array.isArray(individual.s) ? individual.s.join(', ') : (individual.s || 'Aucun')}`);
        
        // Accès O(1) aux familles
        const familyIds = individual.families || [];
        console.log(`  Familles (${familyIds.length}):`);
        
        const familiesWithDetails = [];
        familyIds.forEach(familyId => {
            const family = gedcomDataStore.familiesCache.get(familyId);
            if (family) {
                // Identifier le conjoint dans cette famille
                const spouse = (family.h === personId) ? family.w : family.h;
                console.log(`    ${familyId}: Mari=${family.h}, Épouse=${family.w}, Enfants=${family.c?.length || 0}, Conjoint=${spouse}`);
                
                // Récupérer les événements de cette famille (mariages, divorces)
                if (family.e && family.e.length > 0) {
                    console.log(`      Événements familiaux: ${family.e.length}`);
                    family.e.forEach(event => {
                        const eventType = window.eventCompression?.decompressFamilyEvent ? 
                            window.eventCompression.decompressFamilyEvent(event).type : event.t;
                        const eventDate = event.d ? new Date(String(event.d).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')).toLocaleDateString('fr-FR') : 'Date inconnue';
                        console.log(`        - ${eventType}: ${eventDate}`);
                    });
                }
                
                familiesWithDetails.push({ 
                    id: familyId, 
                    data: family, 
                    spouse: spouse,
                    spouseData: gedcomDataStore.individualsCache.get(spouse)
                });
            }
        });
        
        return {
            individual,
            families: familiesWithDetails
        };
    };
    
    // 🚀 NOUVELLE FONCTION: Analyser spécifiquement les mariages multiples
    window.analyzeMultipleMarriages = (personId) => {
        const analysis = window.analyzeFamilyRelations(personId);
        if (!analysis || analysis.families.length <= 1) {
            console.log(`👤 ${personId} n'a pas de mariages multiples`);
            return analysis;
        }
        
        console.log(`\n💍 ANALYSE MARIAGES MULTIPLES pour ${personId}:`);
        analysis.families.forEach((family, index) => {
            const spouseName = family.spouseData ? `${family.spouseData.fn}` : family.spouse;
            console.log(`\n  Mariage ${index + 1}: avec ${spouseName} (${family.spouse})`);
            console.log(`    Famille: ${family.id}`);
            
            if (family.data.e) {
                const marriages = family.data.e.filter(e => ['fm', 'fmc', 'fmr'].includes(e.t));
                const divorces = family.data.e.filter(e => ['fd', 'fs'].includes(e.t));
                
                marriages.forEach(m => {
                    const date = m.d ? new Date(String(m.d).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')).toLocaleDateString('fr-FR') : 'Date inconnue';
                    console.log(`      💒 Mariage: ${date}`);
                });
                
                divorces.forEach(d => {
                    const date = d.d ? new Date(String(d.d).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')).toLocaleDateString('fr-FR') : 'Date inconnue';
                    console.log(`      💔 Divorce/Séparation: ${date}`);
                });
            }
            
            if (family.data.c && family.data.c.length > 0) {
                console.log(`      👶 Enfants: ${family.data.c.length}`);
                family.data.c.forEach(childId => {
                    const child = gedcomDataStore.individualsCache.get(childId);
                    if (child) {
                        console.log(`        - ${child.fn} (${childId})`);
                    }
                });
            }
        });
        
        return analysis;
    };
    
    // 💾 Sauvegarder familiesCache dans un fichier JSON propre
    window.saveFamiliesCache = (filename = 'familiesCache.json') => {
        if (!gedcomDataStore.familiesCache || gedcomDataStore.familiesCache.size === 0) {
            console.error('❌ Aucune familiesCache trouvée.');
            return;
        }
        
        const familiesObj = Object.fromEntries(gedcomDataStore.familiesCache);
        const jsonString = JSON.stringify(familiesObj, null, 2);
        
        // Créer un blob et télécharger
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ FamiliesCache sauvegardé dans ' + filename);
        console.log('🏠 ' + gedcomDataStore.familiesCache.size + ' familles exportées');
        return true;
    };
    
    // Helpers de debug exposés sur window — annonce condensée pour
    // garder la découvrabilité sans spammer la console à chaque
    // chargement de cache. La liste complète vit dans ce fichier.
    console.log('💡 Helpers cache (window): saveCache(), printCache(), showIndividual("@I…@"), saveFamiliesCache(), saveFamilyTowns(), saveNotesCache() — voir gedcomDataStore.js pour le set complet.');
}

export default gedcomDataStore;